import { create } from 'zustand';
import React from 'react';

// Simple, stable Zustand store for compliance
const useComplianceStore = create((set, get) => ({
  // State
  complianceRecords: [],
  alerts: [],
  loading: false,
  error: null,

  // Actions
  createComplianceRecord: (recordData) => {
    const id = `compliance_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    const record = {
      id,
      type: recordData?.type || 'inspection',
      centreId: recordData?.centreId,
      status: recordData?.status || 'pending',
      dueDate: recordData?.dueDate,
      completedDate: recordData?.completedDate,
      notes: recordData?.notes || '',
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    
    set(state => ({
      complianceRecords: [...state?.complianceRecords, record]
    }));
  },

  createAlert: (alertData) => {
    const id = `alert_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    const alert = {
      id,
      type: alertData?.type || 'warning',
      message: alertData?.message || '',
      centreId: alertData?.centreId,
      resolved: false,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    
    set(state => ({
      alerts: [...state?.alerts, alert]
    }));
  },

  resolveAlert: (id) => {
    set(state => ({
      alerts: state?.alerts?.map(alert => 
        alert?.id === id 
          ? { ...alert, resolved: true, updatedAt: new Date()?.toISOString() }
          : alert
      )
    }));
  },

  // Helper functions
  getComplianceByCentre: (centreId) => {
    const state = get();
    return state?.complianceRecords?.filter(record => record?.centreId === centreId) || [];
  },

  getActiveAlerts: () => {
    const state = get();
    return state?.alerts?.filter(alert => !alert?.resolved) || [];
  },

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error })
}));

export default useComplianceStore;

// Simple hook that uses stable selectors
export const useCompliance = () => {
  const complianceRecords = useComplianceStore(state => state?.complianceRecords);
  const alerts = useComplianceStore(state => state?.alerts);
  const loading = useComplianceStore(state => state?.loading);
  const error = useComplianceStore(state => state?.error);
  const createComplianceRecord = useComplianceStore(state => state?.createComplianceRecord);
  const createAlert = useComplianceStore(state => state?.createAlert);
  const resolveAlert = useComplianceStore(state => state?.resolveAlert);
  const getComplianceByCentre = useComplianceStore(state => state?.getComplianceByCentre);
  const getActiveAlerts = useComplianceStore(state => state?.getActiveAlerts);
  const setLoading = useComplianceStore(state => state?.setLoading);
  const setError = useComplianceStore(state => state?.setError);

  return {
    complianceRecords,
    alerts,
    loading,
    error,
    createComplianceRecord,
    createAlert,
    resolveAlert,
    getComplianceByCentre,
    getActiveAlerts,
    setLoading,
    setError
  };
};